import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useCouponCart } from "@/stores/couponCart";
import { useSubmitCoupon } from "@/hooks/useCoupons";
import { useActiveSubscription } from "@/hooks/useSubscription";
import { COUPON_TYPES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CouponTypePicker } from "./CouponTypePicker";

const HIDE_PATHS = ["/admin", "/auth", "/welcome", "/payment/return"];

export function CouponCart() {
  const location = useLocation();
  // Le TypePicker doit toujours être monté pour pouvoir s'ouvrir.
  // La sticky bar n'apparaît que quand un type a été choisi.
  if (HIDE_PATHS.some((p) => location.pathname.startsWith(p))) return null;
  return (
    <>
      <StickyBar />
      <CouponTypePicker />
    </>
  );
}

function StickyBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type, bets, clear } = useCouponCart();
  const submit = useSubmitCoupon();
  const sub = useActiveSubscription();
  const [openDetails, setOpenDetails] = useState(false);

  if (!type) return null;

  const max = COUPON_TYPES[type].matches;
  const remaining = max - bets.length;
  const complete = remaining === 0;

  async function onSubmit() {
    if (!type) return;
    try {
      const r = await submit.mutateAsync({ type, bets });
      toast.success(t("coupon.submitted"));
      clear();
      setOpenDetails(false);
      navigate(`/coupon/${r.couponId}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("coupon.submitError"));
    }
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 280 }}
          className="fixed inset-x-0 bottom-[68px] z-30 px-4 safe-bottom pointer-events-none"
        >
          <div className="mx-auto max-w-2xl pointer-events-auto">
            <button
              onClick={() => setOpenDetails(true)}
              className={cn(
                "w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-card backdrop-blur-md transition-colors duration-180",
                complete
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card/95 border-white/[0.08]"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn("flex size-9 items-center justify-center rounded-lg", complete ? "bg-white/15" : "bg-primary/10")}>
                  <Trophy className={cn("size-4", complete ? "text-primary-foreground" : "text-primary")} strokeWidth={2.25} />
                </div>
                <div className="text-left">
                  <p className="text-2xs font-medium opacity-80">{t("coupon.cart")} · {type}</p>
                  <p className="text-sm font-semibold num">
                    {t("coupon.cartCount", { n: bets.length, total: max })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!complete && (
                  <Badge variant={complete ? "default" : "secondary"} className="text-2xs">
                    {t("coupon.incomplete", { n: remaining })}
                  </Badge>
                )}
                <ChevronUp className="size-4 opacity-70" />
              </div>
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      <Dialog open={openDetails} onOpenChange={setOpenDetails}>
        <DialogContent className="max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t("coupon.cart")} · {type}</DialogTitle>
            <p className="text-xs text-muted-foreground">
              {t("coupon.cartCount", { n: bets.length, total: max })}
              {sub.data && <span className="ml-2">· Plan {sub.data.plan}</span>}
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 divide-y divide-white/[0.06]">
            {bets.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("coupon.cartEmpty")}</p>
            )}
            {bets.map((b) => (
              <CartRow key={b.matchId} bet={b} />
            ))}
          </div>

          <div className="space-y-2 pt-3 border-t border-white/[0.06] -mx-6 px-6">
            {!sub.data && (
              <p className="text-xs text-warning text-center">
                {t("coupon.needsPlan", { plan: COUPON_TYPES[type].requiredPlan })}
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={clear} className="flex-1">
                {t("common.cancel")}
              </Button>
              <Button
                onClick={onSubmit}
                loading={submit.isPending}
                disabled={!complete}
                className="flex-1"
              >
                {complete ? t("coupon.submit") : t("coupon.incomplete", { n: remaining })}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CartRow({ bet }: { bet: ReturnType<typeof useCouponCart.getState>["bets"][number] }) {
  const { removeBet } = useCouponCart();
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {bet.match.home.name} <span className="text-muted-foreground">vs</span> {bet.match.away.name}
        </p>
        <p className="text-2xs text-muted-foreground mt-0.5">{betLabel(bet.bet, t)}</p>
      </div>
      <Button size="icon-sm" variant="ghost" onClick={() => removeBet(bet.matchId)} aria-label="remove">
        <X className="size-3.5" />
      </Button>
    </div>
  );
}

function betLabel(bet: ReturnType<typeof useCouponCart.getState>["bets"][number]["bet"], t: (k: string) => string): string {
  const ou = (line: number, pick: "over" | "under") => `${pick === "over" ? "+" : "-"}${line}`;
  switch (bet.type) {
    case "result":
      return `${t("matches.betType.result")} → ${bet.pick}`;
    case "doubleChance":
      return `Double chance → ${bet.pick}`;
    case "dnb":
      return `DNB → ${bet.pick}`;
    case "htResult":
      return `Mi-temps 1X2 → ${bet.pick}`;
    case "htft":
      return `Mi-temps/Fin → ${bet.ht}/${bet.ft}`;
    case "score":
      return `${t("matches.betType.score")} → ${bet.home}-${bet.away}`;
    case "goals":
      return `${t("matches.betType.goals")} → ${ou(bet.line, bet.pick)}`;
    case "btts":
      return `${t("matches.betType.btts")} → ${bet.pick === "yes" ? "Oui" : "Non"}`;
    case "htGoals":
      return `Buts mi-temps → ${ou(bet.line, bet.pick)}`;
    case "teamGoals":
      return `Buts ${bet.team === "home" ? "dom" : "ext"} → ${ou(bet.line, bet.pick)}`;
    case "corners":
      return `Corners → ${ou(bet.line, bet.pick)}`;
    case "cards":
      return `Cartons → ${ou(bet.line, bet.pick)}`;
    case "shots":
      return `Tirs → ${ou(bet.line, bet.pick)}`;
    case "fouls":
      return `Fautes → ${ou(bet.line, bet.pick)}`;
  }
}
